// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Runtime configuration written by WebDeployStack as /config.json (design 9).
import { z } from 'zod';

export const runtimeConfigSchema = z.object({
  region: z.string(),
  userPoolId: z.string(),
  userPoolClientId: z.string(),
  cognitoDomain: z.string(),
  apiUrl: z.url(),
  appOrigin: z.url(),
  documentsBucketOrigin: z.url(),
  /** Extraction preview switches (create-doc-interactive design, "config.ts"); defaulted so an older config.json loads. */
  preview: z
    .object({ editorPreview: z.boolean(), autoPreview: z.boolean() })
    .default({ editorPreview: true, autoPreview: false }),
  /**
   * Blueprint sets (blueprint-sets design "Navigation, routes, and config"; Req 14.2). Defaulted to disabled, so an
   * older config.json without `sets` hides every set navigation and action.
   */
  sets: z
    .object({ enabled: z.boolean(), maxSetMembers: z.number(), suggestMaxPages: z.number() })
    .default({ enabled: false, maxSetMembers: 15, suggestMaxPages: 100 }),
});
export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;

let current: RuntimeConfig | undefined;

/**
 * Always same-origin. In local development the Vite dev server proxies /config.json to the deployed
 * distribution (VITE_REMOTE_CONFIG_URL, see apps/web/dev-proxy.ts), so the distribution needs no CORS.
 */
export const CONFIG_URL = '/config.json';

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const res = await fetchImpl(CONFIG_URL, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Could not load ${CONFIG_URL} (${res.status})`);
  current = runtimeConfigSchema.parse(await res.json());
  return current;
}

/** Sets the config (tests); defaults such as `preview` are applied as when loading config.json. */
export function setRuntimeConfig(c: z.input<typeof runtimeConfigSchema>): void {
  current = runtimeConfigSchema.parse(c);
}

export function runtimeConfig(): RuntimeConfig {
  if (!current) throw new Error('Runtime config not loaded');
  return current;
}

/** Whether blueprint sets are turned on in this deployment (Req 14.2); false before the config is loaded. */
export function setsEnabled(): boolean {
  return current?.sets.enabled ?? false;
}
